/**
 * FeedbackButton — inline feedback widget for lesson sections and whole lessons.
 * Expands to a TextInput on press; submits to POST /api/lessons/feedback.
 */

import React, { useState, useCallback } from 'react';
import { TextInput, TouchableOpacity, ActivityIndicator } from 'react-native';
import { Box, HStack, Text } from '@gluestack-ui/themed';
import { useTranslation } from 'react-i18next';
import { FS_XS, FS_SM } from '../../theme';
import { api } from '../../services/api';
import { createLogger } from '../../utils/logger';
import { useDisplayTheme } from '../../hooks/useDisplayTheme';

const log = createLogger('FeedbackButton');

interface FeedbackButtonProps {
  lessonId: string;
  sectionIndex?: number;
  locale: string;
}

export const FeedbackButton = React.memo(function FeedbackButton({
  lessonId,
  sectionIndex,
  locale,
}: FeedbackButtonProps) {
  const { t } = useTranslation();
  const { theme, fontScale, ffs } = useDisplayTheme();

  const [expanded, setExpanded] = useState(false);
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(false);

  const testID = sectionIndex !== undefined
    ? `lesson-feedback-${sectionIndex}`
    : 'lesson-feedback-lesson';

  const placeholder = sectionIndex !== undefined
    ? t('lessons.feedback.sectionPlaceholder')
    : t('lessons.feedback.lessonPlaceholder');

  const handleSubmit = useCallback(async () => {
    if (!text.trim()) return;
    setSubmitting(true);
    setError(false);
    try {
      // submitLessonFeedback returns a Result (it does NOT throw on HTTP
      // errors), so a failed POST must be handled explicitly — otherwise the
      // success state is shown on a failed submit (silent feedback loss).
      const result = await api.submitLessonFeedback({ lessonId, sectionIndex, locale, text: text.trim() });
      if (!result.ok) {
        log.error('Failed to submit feedback', { lessonId, sectionIndex, kind: result.kind });
        setError(true);
        return;
      }
      setSubmitted(true);
      setText('');
      setTimeout(() => {
        setExpanded(false);
        setSubmitted(false);
      }, 2000);
    } catch (err) {
      log.error('Failed to submit feedback', { err, lessonId, sectionIndex });
      setError(true);
    } finally {
      setSubmitting(false);
    }
  }, [text, lessonId, sectionIndex, locale]);

  if (submitted) {
    return (
      <Box testID={`${testID}-success`} px="$3" py="$2">
        <Text
          allowFontScaling={false}
          style={{ fontSize: FS_XS * fontScale, color: theme.success, ...ffs }}
        >
          {t('lessons.feedback.success')}
        </Text>
      </Box>
    );
  }

  if (!expanded) {
    return (
      <TouchableOpacity
        testID={testID}
        accessibilityLabel={t('lessons.feedback.button')}
        accessibilityRole="button"
        onPress={() => setExpanded(true)}
      >
        <Box px="$3" py="$2">
          <Text
            allowFontScaling={false}
            style={{ fontSize: FS_XS * fontScale, color: theme.textSecondary, ...ffs }}
          >
            {t('lessons.feedback.button')}
          </Text>
        </Box>
      </TouchableOpacity>
    );
  }

  return (
    <Box px="$3" py="$2">
      <TextInput
        testID={`${testID}-input`}
        accessibilityLabel={placeholder}
        placeholder={placeholder}
        placeholderTextColor={theme.textSecondary}
        value={text}
        onChangeText={setText}
        multiline
        maxLength={2000}
        style={{
          borderWidth: 1,
          borderColor: theme.border,
          borderRadius: 8,
          padding: 10,
          minHeight: 60,
          fontSize: FS_SM * fontScale,
          color: theme.text,
          backgroundColor: theme.card,
          textAlignVertical: 'top',
          ...(ffs || {}),
        }}
      />
      <HStack space="sm" mt="$2" alignItems="center">
        <TouchableOpacity
          testID={`${testID}-submit`}
          accessibilityLabel={t('lessons.feedback.submit')}
          accessibilityRole="button"
          onPress={handleSubmit}
          disabled={submitting || !text.trim()}
        >
          <Box
            px="$3"
            py="$1"
            rounded="$md"
            style={{
              backgroundColor: text.trim() ? theme.primary : theme.border,
            }}
          >
            {submitting ? (
              <ActivityIndicator size="small" color="#fff" />
            ) : (
              <Text
                allowFontScaling={false}
                style={{ fontSize: FS_XS * fontScale, color: '#fff', fontWeight: '600', ...ffs }}
              >
                {t('lessons.feedback.submit')}
              </Text>
            )}
          </Box>
        </TouchableOpacity>
        <TouchableOpacity
          accessibilityLabel="Cancel"
          accessibilityRole="button"
          onPress={() => { setExpanded(false); setText(''); setError(false); }}
        >
          <Text
            allowFontScaling={false}
            style={{ fontSize: FS_XS * fontScale, color: theme.textSecondary, ...ffs }}
          >
            {t('common.cancel')}
          </Text>
        </TouchableOpacity>
      </HStack>
      {error && (
        <Text
          testID={`${testID}-error`}
          allowFontScaling={false}
          style={{ fontSize: FS_XS * fontScale, color: theme.error, marginTop: 4, ...ffs }}
        >
          {t('lessons.feedback.error')}
        </Text>
      )}
    </Box>
  );
});
